import { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Printer, ShoppingBag, Check, X } from "@/components/icons";
import Portal from "../../components/Portal";
import StatCard from "../../components/StatCard";
import { useRealtimeEvent } from "../../lib/realtime";

const STATUS_LABEL = {
  issued: "Emitida",
  accepted: "Aceptada",
  ready: "Lista para retirar",
  fulfilled: "Despachada",
  rejected: "Rechazada",
  cancelled: "Cancelada",
};

const STATUS_CLASS = {
  issued: "bg-[#DBEAFE] text-[#1B405B]",
  accepted: "bg-amber-100 text-amber-700",
  ready: "bg-indigo-100 text-indigo-700",
  fulfilled: "bg-blue-50 text-[#019A75]",
  rejected: "bg-rose-100 text-rose-700",
  cancelled: "bg-black/10 text-black/60",
};

// Estados que se muestran en el "home" de la farmacia.
// Despachadas / rechazadas / canceladas quedan fuera (ver /farmacia/ventas
// para el histórico).
const ACTIVE_STATUSES = ["issued", "accepted", "ready"];

const MS_IN_24H = 24 * 60 * 60 * 1000;

/** Fecha ISO de la última vez que la receta pasó a 'ready'. */
function readyAt(rx) {
  const events = rx?.status_history || [];
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].status === "ready") return events[i].at;
  }
  return null;
}

/** Segundos que faltan para poder cancelar. Devuelve 0 si ya se puede. */
function remainingToCancel(rx) {
  const at = readyAt(rx);
  if (!at) return null;
  const readyTs = new Date(at).getTime();
  const elapsed = Date.now() - readyTs;
  return Math.max(0, MS_IN_24H - elapsed);
}

/** Formatea "3h 12min" o "27min" a partir de ms restantes. */
function formatRemaining(ms) {
  const totalMin = Math.ceil(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

/** ¿La fecha ISO cae en el día local de hoy? */
function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export default function PharmacyInbox() {
  const [list, setList] = useState([]);
  const [saleFor, setSaleFor] = useState(null);
  const [total, setTotal] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(null); // id → action
  const [, setNowTick] = useState(0); // fuerza re-render cada minuto para el countdown

  const load = () => api.get("/prescriptions").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);

  // Refresca los KPIs y los countdowns cada 60 segundos. Es barato: sólo
  // fuerza un re-render, no vuelve a pedir la lista al servidor.
  useEffect(() => {
    const t = setInterval(() => setNowTick((v) => v + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Refresco EN TIEMPO REAL cuando un doctor emite una receta a esta
  // farmacia. La conexión SSE ya se abre globalmente en RealtimeProvider.
  useRealtimeEvent("prescription_new", (payload) => {
    load();
    toast.success(
      `Nueva receta de ${payload?.doctor_name || "un doctor"}${
        payload?.patient_name ? ` — ${payload.patient_name}` : ""
      }`,
      { duration: 6000 },
    );
    // Ping sonoro sutil (opcional, sin dependencias externas)
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 880;
      g.gain.value = 0.05;
      o.start();
      setTimeout(() => {
        o.stop();
        ctx.close();
      }, 160);
    } catch {
      /* silencio si el navegador bloquea audio autoplay */
    }
  });

  const transition = async (rx, action, extra = null) => {
    setActing(`${rx.id}:${action}`);
    try {
      let path = `/prescriptions/${rx.id}/${action}`;
      let body = undefined;
      if (action === "reject") {
        body = { reason: extra };
      }
      await api.post(path, body);
      toast.success(
        action === "accept"
          ? "Receta aceptada"
          : action === "mark-ready"
            ? "Marcada como lista"
            : action === "cancel"
              ? "Receta cancelada"
              : "Receta rechazada",
      );
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const submitSale = async (e) => {
    e.preventDefault();
    try {
      await api.post("/pharmacy/sales", {
        prescription_id: saleFor.id,
        total_amount: parseFloat(total),
      });
      toast.success("Venta registrada y despacho notificado al doctor");
      setSaleFor(null);
      setTotal("");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (rejectReason.trim().length < 3) {
      toast.error("Escribe un motivo (mínimo 3 caracteres)");
      return;
    }
    await transition(rejectFor, "reject", rejectReason.trim());
    setRejectFor(null);
    setRejectReason("");
  };

  // Solo mostramos recetas ACTIVAS en el home (issued/accepted/ready).
  // Las despachadas/rechazadas/canceladas se archivan en /farmacia/ventas.
  const activeList = list.filter((r) => ACTIVE_STATUSES.includes(r.status));

  // KPIs del DÍA en curso — se reinician automáticamente cada medianoche.
  const todayPending = activeList.filter((r) => r.status === "issued");
  const todayInProgress = activeList.filter((r) =>
    ["accepted", "ready"].includes(r.status),
  );
  const todayFulfilled = list.filter(
    (r) => r.status === "fulfilled" && isToday(r.created_at),
  );
  const todayReceived = list.filter((r) => isToday(r.created_at));

  return (
    <div className="space-y-6" data-testid="pharmacy-inbox">
      <div>
        <h1 className="text-3xl font-extrabold text-[#163144] tracking-tight">
          Recetas activas
        </h1>
        <p className="text-sm text-[#1B405B]/70">
          Solo las recetas pendientes o listas para retirar. Los KPIs se
          reinician cada día a las 00:00. Consulta el histórico en{" "}
          <Link to="/farmacia/ventas" className="font-bold underline">
            Ventas
          </Link>
          .
        </p>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 pt-2">
        <StatCard
          title="Recibidas hoy"
          value={todayReceived.length}
          subtitle="Nuevas del día"
          testid="stat-received"
        />
        <StatCard
          title="Por aceptar"
          value={todayPending.length}
          subtitle="Detalles ocultos"
          active={todayPending.length > 0}
          testid="stat-pending"
        />
        <StatCard
          title="En preparación"
          value={todayInProgress.length}
          subtitle="Aceptadas / Listas"
          testid="stat-inprogress"
        />
        <StatCard
          title="Despachadas hoy"
          value={todayFulfilled.length}
          subtitle="Completadas del día"
          to="/farmacia/ventas"
          testid="stat-fulfilled"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {activeList.map((rx) => {
          const isMasked = rx.status === "issued";
          return (
            <div
              key={rx.id}
              className="bg-white rounded-[28px] p-5 border border-black/[0.06]"
              data-testid={`rx-card-${rx.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase font-bold tracking-widest text-[#1B405B]/70">
                    Receta · {new Date(rx.created_at).toLocaleDateString("es-ES")}
                  </div>
                  <div className="font-bold text-[#163144] text-lg">
                    ID: {rx.id.slice(0, 8).toUpperCase()}
                  </div>
                  {rx.doctor_name && (
                    <div
                      className="text-xs text-[#1B405B]/80 mt-0.5"
                      data-testid={`rx-doctor-${rx.id}`}
                    >
                      Recetada por <b>{rx.doctor_name}</b>
                    </div>
                  )}
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full font-bold ${
                    STATUS_CLASS[rx.status] || STATUS_CLASS.issued
                  }`}
                  data-testid={`rx-status-${rx.id}`}
                >
                  {STATUS_LABEL[rx.status] || rx.status}
                </span>
              </div>

              {isMasked ? (
                <div className="mt-4 bg-[#F5F5F5] rounded-2xl p-4 text-center">
                  <div className="text-4xl font-black text-[#1B405B]/30">
                    {rx.items?.length || 0}
                  </div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#1B405B]/60 mt-1">
                    medicamento{(rx.items?.length || 0) === 1 ? "" : "s"} —
                    detalles ocultos
                  </div>
                  <p className="text-[11px] text-black/50 mt-2">
                    Acepta la orden para ver la receta completa.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                    {rx.items.map((i, k) => (
                      <div
                        key={k}
                        className="text-sm bg-[#F5F5F5] rounded-lg px-3 py-2"
                      >
                        <span className="font-bold text-[#163144]">
                          {i.medication}
                        </span>{" "}
                        · {i.dosage} · {i.frequency} · {i.duration}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {rx.status === "rejected" && rx.rejection_reason && (
                <div className="mt-3 text-xs bg-rose-50 text-rose-700 rounded-xl p-3">
                  <b>Motivo del rechazo:</b> {rx.rejection_reason}
                </div>
              )}

              {rx.status === "ready" && (
                <div className="mt-3 text-xs bg-indigo-50 text-indigo-800 rounded-xl p-3">
                  📦 El paciente puede pasar a retirarla.
                </div>
              )}

              <div className="mt-4 flex gap-2 flex-wrap">
                {!isMasked && (
                  <Link
                    to={`/recetas/${rx.id}/imprimir`}
                    target="_blank"
                    className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white border border-[#1B405B]/20 text-[#1B405B]"
                  >
                    <Printer className="w-3.5 h-3.5" /> Ver / Imprimir
                  </Link>
                )}

                {rx.status === "issued" && (
                  <>
                    <button
                      onClick={() => transition(rx, "accept")}
                      disabled={acting === `${rx.id}:accept`}
                      data-testid={`accept-${rx.id}`}
                      className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#01BC8E] text-white hover:bg-[#019976] disabled:opacity-50"
                    >
                      <Check className="w-3.5 h-3.5" /> Aceptar orden
                    </button>
                    <button
                      onClick={() => setRejectFor(rx)}
                      data-testid={`reject-${rx.id}`}
                      className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-rose-300 text-rose-600 hover:bg-rose-50"
                    >
                      <X className="w-3.5 h-3.5" /> Rechazar
                    </button>
                  </>
                )}

                {rx.status === "accepted" && (
                  <>
                    <button
                      onClick={() => transition(rx, "mark-ready")}
                      disabled={acting === `${rx.id}:mark-ready`}
                      data-testid={`mark-ready-${rx.id}`}
                      className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      📦 Marcar lista
                    </button>
                    <button
                      onClick={() => setSaleFor(rx)}
                      data-testid={`fulfill-${rx.id}`}
                      className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#01BC8E] text-white hover:bg-[#019976]"
                    >
                      <ShoppingBag className="w-3.5 h-3.5" /> Registrar venta
                    </button>
                  </>
                )}

                {rx.status === "ready" && (() => {
                  const remainingMs = remainingToCancel(rx);
                  const canCancel = remainingMs !== null && remainingMs === 0;
                  return (
                    <>
                      <button
                        onClick={() => setSaleFor(rx)}
                        data-testid={`fulfill-${rx.id}`}
                        className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#01BC8E] text-white hover:bg-[#019976]"
                      >
                        <ShoppingBag className="w-3.5 h-3.5" /> Registrar venta
                      </button>
                      {canCancel ? (
                        <button
                          onClick={() => transition(rx, "cancel")}
                          disabled={acting === `${rx.id}:cancel`}
                          data-testid={`cancel-${rx.id}`}
                          className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-black/20 text-black/70 hover:bg-black/5 disabled:opacity-50"
                          title="El paciente no vino a retirarla en 24h"
                        >
                          <X className="w-3.5 h-3.5" /> Cancelar
                        </button>
                      ) : (
                        remainingMs !== null && (
                          <span
                            className="text-[11px] text-black/50 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/[0.03]"
                            title="El botón Cancelar se habilita 24h después de marcar la receta como lista."
                            data-testid={`cancel-cooldown-${rx.id}`}
                          >
                            🕒 Cancelar disponible en {formatRemaining(remainingMs)}
                          </span>
                        )
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          );
        })}
        {activeList.length === 0 && (
          <div className="text-sm text-[#1B405B]/60 col-span-2 text-center py-12">
            No hay recetas activas.
            {list.length > 0 && (
              <>
                {" "}Todas las recetas del histórico están en{" "}
                <Link
                  to="/farmacia/ventas"
                  className="font-bold underline text-[#01BC8E]"
                >
                  Ventas
                </Link>
                .
              </>
            )}
          </div>
        )}
      </div>

      {saleFor && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-md p-4 overflow-y-auto">
            <form
              onSubmit={submitSale}
              className="bg-white rounded-3xl p-6 w-full max-w-md space-y-3 my-4 border border-black/[0.06]"
            >
              <h3 className="text-lg font-bold text-[#163144]">
                Registrar venta
              </h3>
              <p className="text-sm text-[#1B405B]/70">
                Receta {saleFor.id.slice(0, 8).toUpperCase()} ·{" "}
                {saleFor.items.length} medicamento(s)
              </p>
              <input
                required
                type="number"
                step="0.01"
                min="0"
                placeholder="Total de la venta ($)"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                className="w-full glass-input rounded-xl px-4 py-2.5"
                data-testid="sale-total-input"
              />
              <div className="text-xs text-[#1B405B]/70 bg-[#DBEAFE]/50 rounded-xl p-3">
                Al registrar la venta se marca como <b>Despachada</b> y se
                notifica automáticamente al doctor.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setSaleFor(null)}
                  className="px-5 py-2 rounded-full font-bold text-[#1B405B]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl font-bold bg-[#01BC8E] text-white hover:bg-[#019976]"
                  data-testid="sale-submit-button"
                >
                  Registrar
                </button>
              </div>
            </form>
          </div>
        </Portal>
      )}

      {rejectFor && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-md p-4 overflow-y-auto">
            <form
              onSubmit={submitReject}
              className="bg-white rounded-3xl p-6 w-full max-w-md space-y-3 my-4 border border-black/[0.06]"
              data-testid="reject-modal"
            >
              <h3 className="text-lg font-bold text-[#163144]">
                Rechazar receta
              </h3>
              <p className="text-sm text-[#1B405B]/70">
                Explica al doctor por qué no puedes despachar esta receta.
              </p>
              <textarea
                required
                minLength={3}
                maxLength={500}
                rows={4}
                autoFocus
                placeholder="Ej: sin stock del medicamento X, dosis no comercializada, etc."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full glass-input rounded-xl px-4 py-2.5"
                data-testid="reject-reason-input"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setRejectFor(null);
                    setRejectReason("");
                  }}
                  className="px-5 py-2 rounded-full font-bold text-[#1B405B]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl font-bold bg-rose-600 text-white hover:bg-rose-700"
                  data-testid="reject-submit-button"
                >
                  Rechazar
                </button>
              </div>
            </form>
          </div>
        </Portal>
      )}
    </div>
  );
}
